import './datePickerRange.scss'
import templateUrl from './datePickerRange.html'

/**
 * Date Picker Range component.
 *
 * @example
 * <date-picker-range
 *   ng-model="$ctrl.date"
 *   on-change="$ctrl.onDateChange()"
 * ></date-picker-range>
 *
 * @type {ngComponent}
 * @prop {string} label
 * @prop {string} placeholder-from
 * @prop {string} placeholder-to
 * @prop {object} ngModel
 * @prop {string} ngModel.from From filter, in milliseconds
 * @prop {string} ngModel.to To filter, in milliseconds
 * @prop {boolean} [time=false] If true, shows also the time widget
 * @prop {boolean} [time-hide-selects=true] If true, only shows the time dialog button and not the select fields
 * @prop {function} on-change
 * @prop {boolean} max-date-disabled
 * @prop {boolean} [required-from=false]
 * @prop {boolean} [required-to=false]
 */
export const tmrDatePickerRange = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrDatePickerRangeController',
  require: {
    ngModelCtrl: 'ngModel',
  },
  bindings: {
    label: '<',
    placeholderFrom: '<',
    placeholderTo: '<',
    ngModel: '=',
    time: '<?',
    timeHideSelects: '<?',
    onChange: '&',
    maxDateDisabled: '<',
    requiredFrom: '<',
    requiredTo: '<',
  },
}

/**
 * Controller of component {@link tmrDatePickerRangeController}.
 *
 * @type {ngController}
 */
export class tmrDatePickerRangeController {
  static ngType = 'controller'

  constructor($scope) {
    'ngInject'
    Object.assign(this, { $scope })
  }

  $onInit() {
    this.placeholderFromDate = this.placeholderFrom ?? 'RESOURCELIST.FROMDATE'
    this.placeholderToDate = this.placeholderTo ?? 'RESOURCELIST.TODATE'
    this.time = this.time ?? false
    this.timeHideSelects = this.timeHideSelects ?? true

    if (this.ngModel.from)
      this.defaultFrom = new Date(parseInt(this.ngModel.from))
    if (this.ngModel.to) this.defaultTo = new Date(parseInt(this.ngModel.to))

    // Convert default date from url (string in milliseconds) to date object.
    // Also, add timezone to set the correct day.
    this.datePicker = { from: null, to: null }
    if (this.ngModel) {
      if (this.ngModel.from) {
        this.datePicker.from = new Date(this.defaultFrom)
      }
      if (this.ngModel.to) {
        this.datePicker.to = new Date(this.defaultTo)
      }
    }

    // Time.
    if (this.time) {
      this.$scope.$watch(
        '$ctrl.timeModels.from',
        () => this.onChangeTime(),
        true
      )
      this.$scope.$watch('$ctrl.timeModels.to', () => this.onChangeTime(), true)
      this.onChangeTime()
    }

    // set Max date.
    this.maxDate = this.maxDateDisabled ? null : new Date()
  }

  onChangeTime() {
    if (!this.timeModels) {
      let fromHours = this.defaultFrom ? this.defaultFrom.getHours() : 0
      let fromMinutes = this.defaultFrom ? this.defaultFrom.getMinutes() : 0
      let toHours = this.defaultTo ? this.defaultTo.getHours() : 0
      let toMinutes = this.defaultTo ? this.defaultTo.getMinutes() : 0
      this.timeModels = {
        from: new Date(2018, 0, 0, fromHours, fromMinutes, 0, 0),
        to: new Date(2018, 0, 0, toHours, toMinutes, 0, 0),
      }
    }
    this.onChangeDate()
  }

  get hasValueFrom() {
    return (
      this.timeModels.from.getHours() + this.timeModels.from.getMinutes() > 0
    )
  }

  get hasValueTo() {
    return (
      this.timeModels.to.getHours() + this.timeModels.to.getMinutes() > 0 &&
      !(
        this.timeModels.to.getHours() == 23 &&
        this.timeModels.to.getMinutes() == 59
      )
    )
  }

  /**
   * Merges date and time values and converts the result to milliseconds.
   * If there is no time, the start date is set to beginning of day,
   * and the end date is set to end of day.
   *
   * @returns {string}
   */
  onChangeDate() {
    let from = ''
    let to = ''

    this.toMinDateTime = this.datePicker.from

    if (this.datePicker.from) {
      const fromDateTime = new Date(this.datePicker.from)
      if (this.time && this.hasValueFrom) {
        fromDateTime.setHours(this.timeModels.from.getHours())
        fromDateTime.setMinutes(this.timeModels.from.getMinutes())
      }
      from = fromDateTime.getTime().toString()

      // @todo md-datepicker ignores time, should add custom validation
      this.toMinDateTime = fromDateTime
    }

    if (this.datePicker.to) {
      const toDateTime = new Date(this.datePicker.to)

      if (this.time && this.hasValueTo) {
        toDateTime.setHours(this.timeModels.to.getHours())
        toDateTime.setMinutes(this.timeModels.to.getMinutes())
      } else {
        toDateTime.setHours(23, 59, 59, 999)
      }
      to = toDateTime.getTime().toString()
    }

    // Pass up datetimes in milliseconds. Assign properties instead or overwriting
    // ngModel, or parent loses reference and misses the update.
    Object.assign(this.ngModel, { from, to })
    this.onChange()
  }
}
